// =====================================================================
// Employee details modal — read-only view of an employee record.
// Extracted from pages/Employees.tsx without any logic changes.
// =====================================================================

import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Mail,
  Phone,
  Plus,
  Edit,
  FileText,
  IndianRupee,
  Download,
} from "lucide-react";
import {
  downloadJoiningLetter,
  downloadOfferLetter,
  sendOfferLetterEmail,
  sendjoiningLetterEmail,
  downloadSalarySlip,
  sendSalarySlipEmail,
  configureOfferLetter,
  configureJoiningLetter,
  configureExperienceCertificate,
  downloadExperienceCertificate,
  sendExperienceCertificateEmail,
} from "../../api/employees";
import { getDepartmentList } from "../../redux/slices/employee";
import { User as UserType } from "../../types";
import { useToast } from "../../hooks/useToast";
import { state } from "../../_mock/state";
import { dispatch, useSelector } from "../../redux/store";
import termsConditionsApi from "../../api/TermsconditionApi";
import { getPreviousPayrollPeriod } from "../payrollApprovalLogic";

export default function EmployeeDetails({
  employee,
  activeTab: externalActiveTab,
  onTabChange
}: {
  employee: UserType;
  activeTab?: "personal" | "employment" | "documents";
  onTabChange?: (tab: "personal" | "employment" | "documents") => void;
}) {
  const defaultPayrollPeriod = getPreviousPayrollPeriod();
  const [internalActiveTab, setInternalActiveTab] = useState<"personal" | "employment" | "documents">(
    externalActiveTab || "personal"
  );

  const [departmentName, setDepartmentName] = useState("");
  const [designationName, setDesignationName] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Use external tab if provided, otherwise use internal
  const currentActiveTab = externalActiveTab || internalActiveTab;

  const setActiveTab = (tab: "personal" | "employment" | "documents") => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setInternalActiveTab(tab);
    }
  };

  // Offer Letter states
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerConfig, setOfferConfig] = useState({
    validTill: "",
    status: "Pending",
    date: new Date().toISOString().split('T')[0],
    terms_and_conditions: "",
  });
  const [isOfferLoading, setIsOfferLoading] = useState(false);
  const [isOfferSaving, setIsOfferSaving] = useState(false);
  // True once the offer letter has been configured & saved (or hydrated from
  // a previously saved record). Drives the "Configured" badge independently
  // of the live form fields.
  const [isOfferConfigured, setIsOfferConfigured] = useState(false);

  // Joining Letter states
  const [showJoiningModal, setShowJoiningModal] = useState(false);
  const [joiningConfig, setJoiningConfig] = useState({
    probationPeriod: "",
    terms_and_conditions: "",
  });
  const [isJoiningLoading, setIsJoiningLoading] = useState(false);
  const [isJoiningSaving, setIsJoiningSaving] = useState(false);
  // Probation is OPTIONAL, so "configured" can't key off it alone — track
  // it explicitly so a joining letter saved with only T&C still shows as
  // configured and survives a reload.
  const [isJoiningConfigured, setIsJoiningConfigured] = useState(false);

  // Experience / Internship-Completion Certificate states
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [certificateConfig, setCertificateConfig] = useState({
    // "experience" | "internship"; default depends on role below.
    certificateType: (employee as any)?.role?.toLowerCase() === "intern" ? "internship" : "experience",
    lastWorkingDay: "",
    issueDate: new Date().toISOString().split("T")[0],
    performanceNote: "",
    terms_and_conditions: "",
  });
  const [isCertificateLoading, setIsCertificateLoading] = useState(false);
  const [isCertificateSaving, setIsCertificateSaving] = useState(false);
  const [isCertificateConfigured, setIsCertificateConfigured] = useState(false);

  // Salary Slip states
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [showSalaryCheckModal, setShowSalaryCheckModal] = useState(false);
  const [salaryConfig, setSalaryConfig] = useState({
    month: Number(defaultPayrollPeriod.month),
    year: Number(defaultPayrollPeriod.year),
  });
  const [isSalaryLoading, setIsSalaryLoading] = useState(false);
  const [isSalaryEmailLoading, setIsSalaryEmailLoading] = useState(false);
  const [isSalaryConfigured, setIsSalaryConfigured] = useState(false);

  // Email modal states
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailType, setEmailType] = useState<"offer" | "joining" | "salary" | "certificate">("offer");
  const [emailRecipient, setEmailRecipient] = useState(employee.email || "");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  // Add these states inside EmployeeDetails component after existing states
  const [termsList, setTermsList] = useState<any[]>([]);
  const [showAddTermsModal, setShowAddTermsModal] = useState(false);
  const [newTermsData, setNewTermsData] = useState({
    title: '',
    content: ''
  });
  const [isSavingTerms, setIsSavingTerms] = useState(false);
  const [selectedTermsId, setSelectedTermsId] = useState<string>("");

  // Add these states with existing ones
  const [joiningTermsList, setJoiningTermsList] = useState<any[]>([]);
  const [selectedJoiningTermsId, setSelectedJoiningTermsId] = useState<string>("");
  const [showAddJoiningTermsModal, setShowAddJoiningTermsModal] = useState(false);
  const [newJoiningTermsData, setNewJoiningTermsData] = useState({
    title: '',
    content: ''
  });
  const [isSavingJoiningTerms, setIsSavingJoiningTerms] = useState(false);

  const { toast } = useToast();
  const departmentList = useSelector((state: any) => state.employee.departmentList) || [];
  const designationList = useSelector((state: any) => state.employee.designationList) || [];

  useEffect(() => {
    dispatch(getDepartmentList());
  }, []);

  // Add this useEffect inside EmployeeDetails component
  useEffect(() => {
    fetchTermsList();
  }, []);

  // Add these functions inside EmployeeDetails component
  const fetchTermsList = async () => {
    try {
      const response = await termsConditionsApi.getTermsConditionsList();
      let data = [];
      if (response && response.data && Array.isArray(response.data)) {
        data = response.data;
      } else if (response && Array.isArray(response)) {
        data = response;
      } else {
        data = [];
      }
      // Filter only offer letter type
      const offerTerms = data.filter((term: any) => term.letter_type?.toLowerCase() === 'offer');
      setTermsList(offerTerms);
    } catch (error) {
      console.error("Failed to fetch terms:", error);
    }
  };

  const handleSaveNewTerms = async () => {
    if (!newTermsData.title || !newTermsData.content) {
      toast({ // title: "Error", 
        description: "Please fill all required fields", variant: "destructive"
      });
      return;
    }

    setIsSavingTerms(true);
    try {
      const response = await termsConditionsApi.createTermsConditions({
        title: newTermsData.title,
        letter_type: 'offer',
        content: newTermsData.content
      });

      if (response.success !== false) {
        toast({ title: "Success", description: "Terms & Conditions added successfully" });
        await fetchTermsList();
        setShowAddTermsModal(false);
        setNewTermsData({ title: '', content: '' });

        const newTermsId = response.data?.id || response.id;
        if (newTermsId) {
          setSelectedTermsId(String(newTermsId));
          const selectedTerm = termsList.find(t => String(t.id) === String(newTermsId)) || (response.data || response);
          if (selectedTerm) {
            setOfferConfig(prev => ({ ...prev, terms_and_conditions: selectedTerm.content }));
          }
        }
      }
    } catch (error: any) {
      toast({ // title: "Error", 
        description: error.message || error.detail || "Failed to save terms", variant: "destructive"
      });
    } finally {
      setIsSavingTerms(false);
    }
  };

  const handleTermsSelect = (value: string) => {
    if (value === "add_new") {
      setShowAddTermsModal(true);
      setSelectedTermsId("");
    } else {
      setSelectedTermsId(value);  // This stores the ID
      const selectedTerm = termsList.find((term: any) => String(term.id) === value);
      if (selectedTerm) {
        // Store the content for preview only, not for API
        setOfferConfig(prev => ({ ...prev, terms_and_conditions: selectedTerm.content }));
      }
    }
  };

  // Add this useEffect to fetch joining terms
  useEffect(() => {
    fetchJoiningTermsList();
  }, []);

  // Function to fetch joining letter terms
  const fetchJoiningTermsList = async () => {
    try {
      const response = await termsConditionsApi.getTermsConditionsList();
      let data = [];
      if (response && response.data && Array.isArray(response.data)) {
        data = response.data;
      } else if (response && Array.isArray(response)) {
        data = response;
      } else {
        data = [];
      }
      // Filter only joining letter type
      const joiningTerms = data.filter((term: any) => term.letter_type?.toLowerCase() === 'joining');
      setJoiningTermsList(joiningTerms);
    } catch (error) {
      console.error("Failed to fetch joining terms:", error);
    }
  };

  // Function to save new joining terms
  const handleSaveNewJoiningTerms = async () => {
    if (!newJoiningTermsData.title || !newJoiningTermsData.content) {
      toast({ // title: "Error", 
        description: "Please fill all required fields", variant: "destructive"
      });
      return;
    }

    setIsSavingJoiningTerms(true);
    try {
      const response = await termsConditionsApi.createTermsConditions({
        title: newJoiningTermsData.title,
        letter_type: 'joining',
        content: newJoiningTermsData.content
      });

      if (response.success !== false) {
        toast({ title: "Success", description: "Terms & Conditions added successfully" });
        await fetchJoiningTermsList();
        setShowAddJoiningTermsModal(false);
        setNewJoiningTermsData({ title: '', content: '' });

        const newTermsId = response.data?.id || response.id;
        if (newTermsId) {
          setSelectedJoiningTermsId(String(newTermsId));
          const selectedTerm = joiningTermsList.find(t => String(t.id) === String(newTermsId)) || (response.data || response);
          if (selectedTerm) {
            setJoiningConfig(prev => ({ ...prev, terms_and_conditions: selectedTerm.content }));
          }
        }
      }
    } catch (error: any) {
      toast({ // title: "Error",
        description: error.message || error.detail || "Failed to save terms", variant: "destructive"
      });
    } finally {
      setIsSavingJoiningTerms(false);
    }
  };

  // Function to handle joining terms selection
  const handleJoiningTermsSelect = (value: string) => {
    if (value === "add_new") {
      setShowAddJoiningTermsModal(true);
      setSelectedJoiningTermsId("");
    } else {
      setSelectedJoiningTermsId(value);
      const selectedTerm = joiningTermsList.find((term: any) => String(term.id) === value);
      if (selectedTerm) {
        setJoiningConfig(prev => ({ ...prev, terms_and_conditions: selectedTerm.content }));
      }
    }
  };

  // Hydrate the Offer / Joining "Configure" modals from the persisted
  // employee record so the last-saved values pre-fill when the admin reopens
  // them (instead of resetting to blank). Re-runs when the terms lists load
  // so we can map the stored terms *content* back to its dropdown id.
  // `date` inputs need YYYY-MM-DD, so ISO timestamps are trimmed at the "T".
  useEffect(() => {
    const emp = employee as any;
    if (!emp) return;
    // Don't overwrite values the admin is actively editing in an open modal.
    if (showOfferModal || showJoiningModal || showCertificateModal) return;
    const toDateInput = (v?: string) => (v ? String(v).split("T")[0] : "");

    const offerValidTill = toDateInput(emp.offer_valid_till);
    const offerDate = toDateInput(emp.offer_date);
    // Offer and joining now have SEPARATE stored terms; fall back to the legacy
    // shared column only when a letter-specific one isn't present yet.
    const offerStoredTerms = emp.offer_terms_and_conditions || emp.terms_and_conditions || "";
    const joiningStoredTerms = emp.joining_terms_and_conditions || emp.terms_and_conditions || "";

    setOfferConfig((prev) => ({
      ...prev,
      validTill: offerValidTill,
      date: offerDate || prev.date,
      status: emp.offer_status || prev.status,
    }));
    // An offer counts as configured once a Valid Till has been persisted.
    setIsOfferConfigured(Boolean(offerValidTill));

    const probation = toDateInput(emp.joining_letter_probation_period);
    setJoiningConfig((prev) => ({ ...prev, probationPeriod: probation }));

    // Map each letter's OWN stored terms back to its dropdown selection.
    const offerTerm = termsList.find((t: any) => t.content === offerStoredTerms);
    if (offerTerm) {
      setSelectedTermsId(String(offerTerm.id));
      setOfferConfig((prev) => ({ ...prev, terms_and_conditions: offerTerm.content }));
    }
    const joiningTerm = joiningTermsList.find((t: any) => t.content === joiningStoredTerms);
    if (joiningTerm) {
      setSelectedJoiningTermsId(String(joiningTerm.id));
      setJoiningConfig((prev) => ({ ...prev, terms_and_conditions: joiningTerm.content }));
    }

    // A joining letter is "configured" once a probation date and/or its own
    // T&C has been saved (sent_at also implies it was generated earlier).
    setIsJoiningConfigured(
      Boolean(probation) || Boolean(joiningTerm) || Boolean(emp.joining_letter_sent_at),
    );

    // Experience / internship certificate config lives in its own table,
    // returned as emp.CertificateConfig (or null when never configured).
    const cert = emp.CertificateConfig;
    if (cert) {
      const certLastDay = toDateInput(cert.last_working_day);
      setCertificateConfig((prev) => ({
        ...prev,
        certificateType: cert.certificate_type || prev.certificateType,
        lastWorkingDay: certLastDay,
        issueDate: toDateInput(cert.issue_date) || prev.issueDate,
        performanceNote: cert.performance_note || "",
        terms_and_conditions: cert.terms_and_conditions || "",
      }));
      setIsCertificateConfigured(
        Boolean(certLastDay) || Boolean(cert.sent_at),
      );
    }
    // Keyed on the persisted fields (not the whole object) so an unrelated
    // parent re-render doesn't reset the form; the open-modal guard above
    // protects in-progress edits.
  }, [
    (employee as any)?.id,
    (employee as any)?.offer_valid_till,
    (employee as any)?.offer_date,
    (employee as any)?.offer_status,
    (employee as any)?.terms_and_conditions,
    (employee as any)?.offer_terms_and_conditions,
    (employee as any)?.joining_terms_and_conditions,
    (employee as any)?.joining_letter_probation_period,
    (employee as any)?.joining_letter_sent_at,
    (employee as any)?.CertificateConfig,
    termsList,
    joiningTermsList,
  ]);

  // Always resolve the LIVE department / designation name from the master
  // lists keyed by id, so renaming a department or designation in settings
  // reflects automatically here. Falls back to the denormalised string the
  // employee was saved with when the id-based lookup misses.
  useEffect(() => {
    const byId = employee?.department_id && departmentList.length > 0
      ? departmentList.find((d: { id: string }) => d.id === employee.department_id)?.name
      : '';
    setDepartmentName(byId || (employee as any)?.department || '-');
  }, [employee?.department_id, (employee as any)?.department, departmentList]);

  useEffect(() => {
    const byId = employee?.designation_id && designationList.length > 0
      ? designationList.find((d: { id: string }) => d.id === employee.designation_id)?.name
      : '';
    setDesignationName(byId || (employee as any)?.designation || '-');
  }, [employee?.designation_id, (employee as any)?.designation, designationList]);

  const getMonthName = (month: number): string => {
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return monthNames[month - 1];
  };

  const openOfferConfig = () => setShowOfferModal(true);
  const openJoiningConfig = () => setShowJoiningModal(true);
  const openCertificateConfig = () => setShowCertificateModal(true);

  // Label varies by role/selection: interns get an "Internship Completion
  // Certificate", everyone else an "Experience Letter".
  const certificateLabel =
    certificateConfig.certificateType === "internship"
      ? "Internship Certificate"
      : "Experience Letter";

  const saveCertificateConfig = async () => {
    if (!certificateConfig.lastWorkingDay) {
      toast({ description: "Please select the Last Working Day", variant: "destructive" });
      return;
    }
    setIsCertificateSaving(true);
    try {
      await configureExperienceCertificate(
        employee.id,
        certificateConfig.certificateType,
        certificateConfig.lastWorkingDay,
        certificateConfig.issueDate,
        certificateConfig.performanceNote,
        undefined,
      );
      setIsCertificateConfigured(true);
      setShowCertificateModal(false);
      toast({ title: "Success", description: `${certificateLabel} configuration saved` });
    } catch (error: any) {
      toast({
        description: error.message || "Failed to save certificate configuration",
        variant: "destructive",
      });
    } finally {
      setIsCertificateSaving(false);
    }
  };

  const handleDownloadCertificate = async () => {
    if (!certificateConfig.lastWorkingDay) {
      openCertificateConfig();
      return;
    }
    setIsCertificateLoading(true);
    try {
      await downloadExperienceCertificate(
        employee.id,
        certificateConfig.certificateType,
        certificateConfig.lastWorkingDay,
        certificateConfig.issueDate,
        certificateConfig.performanceNote,
        undefined,
      );
      toast({ title: "Success", description: `${certificateLabel} downloaded successfully` });
    } catch (error: any) {
      toast({
        description: error.message || error.detail || "Failed to download",
        variant: "destructive",
      });
    } finally {
      setIsCertificateLoading(false);
    }
  };

  const openSalaryConfig = () => {
    setShowSalaryCheckModal(true);
  };

  const saveOfferConfig = async () => {
    if (!offerConfig.validTill) {
      toast({ // title: "Error",
        description: "Please select Offer Valid Till date", variant: "destructive"
      });
      return;
    }
    setIsOfferSaving(true);
    try {
      // Persist so the config survives a reload and pre-fills next time.
      await configureOfferLetter(
        employee.id,
        offerConfig.validTill,
        offerConfig.date,
        offerConfig.status,
        selectedTermsId && selectedTermsId !== "add_new" ? selectedTermsId : undefined,
      );
      setIsOfferConfigured(true);
      setShowOfferModal(false);
      toast({ title: "Success", description: "Offer letter configuration saved" });
    } catch (error: any) {
      toast({
        description: error.message || "Failed to save offer configuration",
        variant: "destructive",
      });
    } finally {
      setIsOfferSaving(false);
    }
  };

  const saveJoiningConfig = async () => {
    // Probation period is OPTIONAL — no validation here.
    setIsJoiningSaving(true);
    try {
      await configureJoiningLetter(
        employee.id,
        joiningConfig.probationPeriod || undefined,
        selectedJoiningTermsId && selectedJoiningTermsId !== "add_new"
          ? selectedJoiningTermsId
          : undefined,
      );
      setIsJoiningConfigured(true);
      setShowJoiningModal(false);
      toast({ title: "Success", description: "Joining letter configuration saved" });
    } catch (error: any) {
      toast({
        description: error.message || "Failed to save joining configuration",
        variant: "destructive",
      });
    } finally {
      setIsJoiningSaving(false);
    }
  };

  const saveSalaryConfig = () => {
    if (!salaryConfig.month || !salaryConfig.year) {
      toast({ // title: "Error",
        description: "Please select month and year", variant: "destructive"
      });
      return;
    }
    setShowSalaryModal(false);
    setIsSalaryConfigured(true);
    toast({ title: "Success", description: "Salary slip configuration saved" });
  };

  const handleDownloadOfferLetter = async () => {
    if (!offerConfig.validTill) {
      openOfferConfig();
      return;
    }
    // if (!selectedTermsId) {
    //   toast({ // title: "Error",
    //     description: "Please select Terms & Conditions first", variant: "destructive" });
    //   return;
    // }
    setIsOfferLoading(true);
    try {
      // Send the tc_id, not the description
      await downloadOfferLetter(
        employee.id,
        offerConfig.validTill,
        offerConfig.date,
        offerConfig.status,
        selectedTermsId  // This sends the ID, not the description
      );
      toast({ title: "Success", description: "Offer letter downloaded successfully" });
    } catch (error: any) {
      toast({ // title: "Error",
        description: error.message || "Failed to download", variant: "destructive"
      });
    } finally {
      setIsOfferLoading(false);
    }
  };

  // const handleDownloadJoiningLetter = async () => {
  //   if (!joiningConfig.probationPeriod) {
  //     openJoiningConfig();
  //     return;
  //   }
  //   setIsJoiningLoading(true);
  //   try {
  //     await downloadJoiningLetter(employee.id, joiningConfig.probationPeriod, selectedTermsId);
  //     toast({ title: "Success", description: "Joining letter downloaded successfully" });
  //   } catch (error: any) {
  //     toast({ title: "Error", description: error.message || "Failed to download", variant: "destructive" });
  //   } finally {
  //     setIsJoiningLoading(false);
  //   }
  // };

  const handleDownloadJoiningLetter = async () => {
    // if (!joiningConfig.probationPeriod) {
    //   openJoiningConfig();
    //   return;
    // }
    // if (!selectedJoiningTermsId) {
    //   toast({ // title: "Error",
    //     description: "Please select Terms & Conditions first", variant: "destructive" });
    //   return;
    // }
    setIsJoiningLoading(true);
    try {
      await downloadJoiningLetter(employee.id, joiningConfig.probationPeriod, selectedJoiningTermsId);
      toast({ title: "Success", description: "Joining letter downloaded successfully" });
    } catch (error: any) {
      toast({ // title: "Error", 
        description: error.message || error.detail || "Failed to download", variant: "destructive"
      });
    } finally {
      setIsJoiningLoading(false);
    }
  };

  const handleDownloadSalarySlip = async () => {
    if (!isSalaryConfigured || !salaryConfig.month || !salaryConfig.year) {
      toast({ title: "Configuration Required", description: "Please configure salary slip first", variant: "destructive" });
      openSalaryConfig();
      return;
    }
    setIsSalaryLoading(true);
    try {
      await downloadSalarySlip(employee.id, salaryConfig.month, salaryConfig.year);
      toast({ title: "Success", description: `Salary slip for ${getMonthName(salaryConfig.month)} ${salaryConfig.year} downloaded` });
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to download", variant: "destructive" });
    } finally {
      setIsSalaryLoading(false);
    }
  };

  const handleSendOfferEmail = async () => {
    if (!offerConfig.validTill) {
      toast({ title: "Configuration Required", description: "Please configure offer letter first", variant: "destructive" });
      openOfferConfig();
      return;
    }
    // if (!selectedTermsId) {
    //   toast({ // title: "Error",
    //     description: "Please select Terms & Conditions first", variant: "destructive" });
    //   return;
    // }
    if (!emailRecipient) {
      toast({ // title: "Error",
        description: "Please enter recipient email", variant: "destructive"
      });
      return;
    }
    setIsSendingEmail(true);
    try {
      // Send the tc_id, not the description
      await sendOfferLetterEmail(
        employee.id,
        offerConfig.validTill,
        offerConfig.date,
        offerConfig.status,
        selectedTermsId  // This sends the ID, not the description
      );
      toast({ title: "Success", description: `Offer letter sent to ${emailRecipient}` });
      setShowEmailModal(false);
    } catch (error: any) {
      toast({ // title: "Error", 
        description: error.message || error.detail || "Failed to send", variant: "destructive"
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  // const handleSendJoiningEmail = async () => {
  //   if (!joiningConfig.probationPeriod) {
  //     toast({ title: "Configuration Required", description: "Please configure joining letter first", variant: "destructive" });
  //     openJoiningConfig();
  //     return;
  //   }
  //   if (!emailRecipient) {
  //     toast({ title: "Error", description: "Please enter recipient email", variant: "destructive" });
  //     return;
  //   }
  //   setIsSendingEmail(true);
  //   try {
  //     await sendjoiningLetterEmail(employee.id, joiningConfig.probationPeriod, joiningConfig.terms_and_conditions);
  //     toast({ title: "Success", description: `Joining letter sent to ${emailRecipient}` });
  //     setShowEmailModal(false);
  //   } catch (error: any) {
  //     toast({ title: "Error", description: error.message || "Failed to send", variant: "destructive" });
  //   } finally {
  //     setIsSendingEmail(false);
  //   }
  // };

  const handleSendJoiningEmail = async () => {
    // if (!joiningConfig.probationPeriod) {
    //   toast({ title: "Configuration Required", description: "Please configure joining letter first", variant: "destructive" });
    //   openJoiningConfig();
    //   return;
    // }
    // if (!selectedJoiningTermsId) {
    //   toast({ // title: "Error", 
    //     description: "Please select Terms & Conditions first", variant: "destructive" });
    //   return;
    // }
    if (!emailRecipient) {
      toast({ // title: "Error",
        description: "Please enter recipient email", variant: "destructive"
      });
      return;
    }
    setIsSendingEmail(true);
    try {
      await sendjoiningLetterEmail(employee.id, joiningConfig.probationPeriod, selectedJoiningTermsId);
      toast({ title: "Success", description: `Joining letter sent to ${emailRecipient}` });
      setShowEmailModal(false);
    } catch (error: any) {
      toast({ // title: "Error",
        description: error.message || error.detail || "Failed to send", variant: "destructive"
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleSendSalaryEmail = async () => {
    if (!isSalaryConfigured || !salaryConfig.month || !salaryConfig.year) {
      toast({ title: "Configuration Required", description: "Please configure salary slip first", variant: "destructive" });
      openSalaryConfig();
      return;
    }
    if (!emailRecipient) {
      toast({ // title: "Error",
        description: "Please enter recipient email", variant: "destructive"
      });
      return;
    }
    setIsSalaryEmailLoading(true);
    try {
      await sendSalarySlipEmail(employee.id, salaryConfig.month, salaryConfig.year);
      toast({ title: "Success", description: `Salary slip for ${getMonthName(salaryConfig.month)} ${salaryConfig.year} sent to ${emailRecipient}` });
      setShowEmailModal(false);
    } catch (error: any) {
      toast({ // title: "Error",
        description: error.message || error.detail || "Failed to send", variant: "destructive"
      });
    } finally {
      setIsSalaryEmailLoading(false);
    }
  };

  const handleSendCertificateEmail = async () => {
    if (!isCertificateConfigured || !certificateConfig.lastWorkingDay) {
      toast({ title: "Configuration Required", description: `Please configure ${certificateLabel.toLowerCase()} first`, variant: "destructive" });
      openCertificateConfig();
      return;
    }
    if (!emailRecipient) {
      toast({ description: "Please enter recipient email", variant: "destructive" });
      return;
    }
    setIsCertificateLoading(true);
    try {
      await sendExperienceCertificateEmail(
        employee.id,
        certificateConfig.certificateType,
        certificateConfig.lastWorkingDay,
        certificateConfig.issueDate,
        certificateConfig.performanceNote,
        undefined,
      );
      toast({ title: "Success", description: `${certificateLabel} sent to ${emailRecipient}` });
      setShowEmailModal(false);
    } catch (error: any) {
      toast({ description: error.message || error.detail || "Failed to send", variant: "destructive" });
    } finally {
      setIsCertificateLoading(false);
    }
  };

  const openEmailModal = (type: "offer" | "joining" | "salary" | "certificate") => {
    if (type === "offer" && !offerConfig.validTill) {
      toast({ title: "Configuration Required", description: "Please configure offer letter first", variant: "destructive" });
      openOfferConfig();
      return;
    }
    if (type === "joining" && !isJoiningConfigured) {
      toast({ title: "Configuration Required", description: "Please configure joining letter first", variant: "destructive" });
      openJoiningConfig();
      return;
    }
    if (type === "certificate" && !isCertificateConfigured) {
      toast({ title: "Configuration Required", description: `Please configure ${certificateLabel.toLowerCase()} first`, variant: "destructive" });
      openCertificateConfig();
      return;
    }
    if (type === "salary" && (!isSalaryConfigured || !salaryConfig.month || !salaryConfig.year)) {
      toast({ title: "Configuration Required", description: "Please configure salary slip first", variant: "destructive" });
      openSalaryConfig();
      return;
    }
    setEmailType(type);
    setEmailRecipient(employee.email || "");
    setShowEmailModal(true);
  };

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: getMonthName(i + 1) }));
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  return (
    <>
      <Tabs value={currentActiveTab} onValueChange={(value) => setActiveTab(value as any)} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="personal">Personal Info</TabsTrigger>
          <TabsTrigger value="employment">Employment</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="personal" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Full Name</Label><div className="p-2 bg-gray-50 rounded text-sm">{[employee.first_name, employee.last_name].filter(Boolean).join(" ")}</div></div>
            <div><Label>Email</Label><div className="p-2 bg-gray-50 rounded text-sm">{employee.email}</div></div>
            <div><Label>Phone</Label><div className="p-2 bg-gray-50 rounded text-sm">{employee.phone || "N/A"}</div></div>
            <div><Label>Employee ID</Label><div className="p-2 bg-gray-50 rounded text-sm">{employee.employeeID}</div></div>
            <div><Label>Date of Birth</Label><div className="p-2 bg-gray-50 rounded text-sm">{employee.date_of_birth || "N/A"}</div></div>
            <div><Label>Emergency Contact</Label><div className="p-2 bg-gray-50 rounded text-sm">{employee.emergency_contacts || "N/A"}</div></div>
          </div>
        </TabsContent>

        <TabsContent value="employment" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Department</Label><div className="p-2 bg-gray-50 rounded text-sm">{departmentName}</div></div>
            <div><Label>Designation</Label><div className="p-2 bg-gray-50 rounded text-sm">{designationName || employee.designation || '-'}</div></div>
            <div><Label>Role</Label><div className="p-2 bg-gray-50 rounded text-sm capitalize">{employee.role}</div></div>
            {/* Guarded because Date of Joining is optional: `new Date(null)` is
                the epoch, which read as "1/1/1970" for anyone imported without
                one or whose date was cleared in the edit panel. */}
            <div><Label>Date of Joining</Label><div className="p-2 bg-gray-50 rounded text-sm">{(() => {
              const d = employee.hire_date ? new Date(employee.hire_date) : null;
              return d && !isNaN(d.getTime()) ? d.toLocaleDateString() : '-';
            })()}</div></div>
            <div><Label>Status</Label><div className="p-2 bg-gray-50 rounded"><Badge variant={employee.is_active ? "default" : "secondary"}>{employee.is_active ? "Active" : "Inactive"}</Badge></div></div>
            <div><Label>Tax Regime</Label><div className="p-2 bg-gray-50 rounded text-sm capitalize">{employee.taxRegime || "New"} Regime</div></div>
          </div>
        </TabsContent>

        <TabsContent value="documents" className="space-y-4 mt-4">
          {/* Offer Letter Card */}
          <div className="border rounded-lg overflow-hidden">
            <div className={`px-4 py-3 ${isOfferConfigured ? 'bg-blue-100' : 'bg-blue-50'} border-b flex justify-between items-center`}>
              <div>
                <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Offer Letter
                </h3>
                <p className="text-xs text-blue-600">Generate and send offer letter</p>
              </div>
              {isOfferConfigured && <Badge className="bg-green-500 text-white text-xs">Configured</Badge>}
            </div>
            <div className="p-4">
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={openOfferConfig} className="flex-1 text-sm">
                  <Edit className="h-3 w-3 mr-1" /> {isOfferConfigured ? "Edit" : "Configure"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleDownloadOfferLetter} disabled={!offerConfig.validTill} className="flex-1 text-sm">
                  {isOfferLoading ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div> : <Download className="h-3 w-3 mr-1" />}
                  Generate &amp; Download
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEmailModal("offer")} disabled={!offerConfig.validTill} className="flex-1 text-sm">
                  <Mail className="h-3 w-3 mr-1" /> Send
                </Button>
              </div>
              {isOfferConfigured && (
                <div className="mt-2 space-y-0.5">
                  {offerConfig.date && (
                    <p className="text-xs text-blue-600">Offer date: {new Date(offerConfig.date).toLocaleDateString()}</p>
                  )}
                  {offerConfig.validTill && (
                    <p className="text-xs text-green-600">Valid till: {new Date(offerConfig.validTill).toLocaleDateString()}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Joining Letter Card */}
          <div className="border rounded-lg overflow-hidden">
            <div className={`px-4 py-3 ${isJoiningConfigured ? 'bg-green-100' : 'bg-green-50'} border-b flex justify-between items-center`}>
              <div>
                <h3 className="font-semibold text-green-800 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Joining Letter
                </h3>
                <p className="text-xs text-green-600">Generate and send joining letter</p>
              </div>
              {isJoiningConfigured && <Badge className="bg-green-500 text-white text-xs">Configured</Badge>}
            </div>
            <div className="p-4">
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={openJoiningConfig} className="flex-1 text-sm">
                  <Edit className="h-3 w-3 mr-1" /> {isJoiningConfigured ? "Edit" : "Configure"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleDownloadJoiningLetter} className="flex-1 text-sm">
                  {isJoiningLoading ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div> : <Download className="h-3 w-3 mr-1" />}
                  Generate &amp; Download
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEmailModal("joining")} className="flex-1 text-sm">
                  <Mail className="h-3 w-3 mr-1" /> Send
                </Button>
              </div>
              {isJoiningConfigured && (
                <p className="text-xs text-green-600 mt-2">
                  {joiningConfig.probationPeriod
                    ? `Probation ends: ${new Date(joiningConfig.probationPeriod).toLocaleDateString()}`
                    : "No probation period"}
                </p>
              )}
            </div>
          </div>

          {/* Experience / Internship-Completion Certificate Card */}
          <div className="border rounded-lg overflow-hidden">
            <div className={`px-4 py-3 ${isCertificateConfigured ? 'bg-amber-100' : 'bg-amber-50'} border-b flex justify-between items-center`}>
              <div>
                <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {certificateLabel}
                </h3>
                <p className="text-xs text-amber-600">Generate and send experience / internship certificate</p>
              </div>
              {isCertificateConfigured && <Badge className="bg-green-500 text-white text-xs">Configured</Badge>}
            </div>
            <div className="p-4">
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={openCertificateConfig} className="flex-1 text-sm">
                  <Edit className="h-3 w-3 mr-1" /> {isCertificateConfigured ? "Edit" : "Configure"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleDownloadCertificate} disabled={!certificateConfig.lastWorkingDay} className="flex-1 text-sm">
                  {isCertificateLoading ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div> : <Download className="h-3 w-3 mr-1" />}
                  Generate &amp; Download
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEmailModal("certificate")} disabled={!isCertificateConfigured} className="flex-1 text-sm">
                  <Mail className="h-3 w-3 mr-1" /> Send
                </Button>
              </div>
              {isCertificateConfigured && certificateConfig.lastWorkingDay && (
                <p className="text-xs text-amber-700 mt-2">Last working day: {new Date(certificateConfig.lastWorkingDay).toLocaleDateString()}</p>
              )}
            </div>
          </div>


          {/* Salary Slip Card — hidden per request via the false guard below. Flip to true to restore. */}
          {false && (
          <div className="border rounded-lg overflow-hidden">
            <div className={`px-4 py-3 ${isSalaryConfigured && salaryConfig.month && salaryConfig.year ? 'bg-purple-100' : 'bg-purple-50'} border-b flex justify-between items-center`}>
              <div>
                <h3 className="font-semibold text-purple-800 flex items-center gap-2">
                  <IndianRupee className="h-4 w-4" />
                  Salary Slip
                </h3>
                <p className="text-xs text-purple-600">Generate and send salary slip</p>
              </div>
              {isSalaryConfigured && salaryConfig.month && salaryConfig.year && <Badge className="bg-green-500 text-white text-xs">Configured</Badge>}
            </div>
            <div className="p-4">
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={openSalaryConfig} className="flex-1 text-sm">
                  <Edit className="h-3 w-3 mr-1" /> {isSalaryConfigured && salaryConfig.month && salaryConfig.year ? "Edit" : "Configure"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDownloadSalarySlip}
                  disabled={!isSalaryConfigured || !salaryConfig.month || !salaryConfig.year}
                  className="flex-1 text-sm"
                >
                  {isSalaryLoading ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div> : <Download className="h-3 w-3 mr-1" />}
                  Generate &amp; Download
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEmailModal("salary")}
                  disabled={!isSalaryConfigured || !salaryConfig.month || !salaryConfig.year}
                  className="flex-1 text-sm"
                >
                  {isSalaryEmailLoading ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div> : <Mail className="h-3 w-3 mr-1" />}
                  Send
                </Button>
              </div>
              {isSalaryConfigured && salaryConfig.month && salaryConfig.year && (
                <p className="text-xs text-purple-600 mt-2">{getMonthName(salaryConfig.month)} {salaryConfig.year}</p>
              )}
            </div>
          </div>
          )}

          {(employee as any)?.retainer?.enabled && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 mt-4">
              <h3 className="font-semibold mb-2">Retainer-ship</h3>
              <p className="text-sm text-gray-600 mb-3">
                ₹{Number((employee as any).retainer.amount).toLocaleString('en-IN')}/mo · {(employee as any).retainer.period_months} months · {(employee as any).retainer.recurring ? 'recurring' : 'one-time'} · status {(employee as any).retainer.status}
              </p>
              <div className="overflow-auto max-h-[50vh]">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500"><th className="sticky top-0 z-10 bg-white py-1">Period</th><th className="sticky top-0 z-10 bg-white">Withheld</th><th className="sticky top-0 z-10 bg-white">Released</th><th className="sticky top-0 z-10 bg-white">Balance</th><th className="sticky top-0 z-10 bg-white">Settled</th></tr></thead>
                <tbody>
                  {((employee as any).retainerLedger || []).map((r: any, i: number) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="py-1">{r.year ? `${r.month}/${r.year}` : r.kind}</td>
                      <td>{r.withheld ? `₹${Number(r.withheld).toLocaleString('en-IN')}` : '—'}</td>
                      <td>{r.released ? `₹${Number(r.released).toLocaleString('en-IN')}` : '—'}</td>
                      <td>₹{Number(r.balance_after).toLocaleString('en-IN')}</td>
                      <td>{r.settled ? '✓' : '·'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* All Modals remain the same as before */}
      {/* Offer Letter Modal */}
      {/* <Dialog open={showOfferModal} onOpenChange={setShowOfferModal}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="text-base">Configure Offer Letter</DialogTitle>
            <DialogDescription className="text-xs">Set offer letter details</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Offer Date</Label>
              <Input type="date" value={offerConfig.date} onChange={(e) => setOfferConfig({ ...offerConfig, date: e.target.value })} className="mt-1 h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Valid Till <span className="text-red-500">*</span></Label>
              <Input type="date" value={offerConfig.validTill} onChange={(e) => setOfferConfig({ ...offerConfig, validTill: e.target.value })} className="mt-1 h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={offerConfig.status} onValueChange={(value) => setOfferConfig({ ...offerConfig, status: value })}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Accepted">Accepted</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                  <SelectItem value="Expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Terms & Conditions</Label>
              <Input type="text" value={offerConfig.terms_and_conditions} onChange={(e) => setOfferConfig({ ...offerConfig, terms_and_conditions: e.target.value })} className="mt-1 h-8 text-sm" placeholder="Enter terms and conditions..." />
            </div>
          </div>
          <DialogFooter><Button size="sm" onClick={saveOfferConfig} className="bg-blue-600">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog> */}
      {/* Offer Letter Modal - Updated with Terms & Conditions Dropdown */}
      <Dialog open={showOfferModal} onOpenChange={setShowOfferModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-base">Configure Offer Letter</DialogTitle>
            <DialogDescription className="text-xs">Set offer letter details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Offer Date</Label>
              <Input
                type="date"
                value={offerConfig.date}
                onChange={(e) => setOfferConfig({ ...offerConfig, date: e.target.value })}
                className="mt-1 h-8 text-sm"
              />
            </div>

            <div>
              <Label className="text-xs">Valid Till <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={offerConfig.validTill}
                onChange={(e) => setOfferConfig({ ...offerConfig, validTill: e.target.value })}
                className="mt-1 h-8 text-sm"
              />
            </div>

            <div>
              <Label className="text-xs">Status</Label>
              <Select value={offerConfig.status} onValueChange={(value) => setOfferConfig({ ...offerConfig, status: value })}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Accepted">Accepted</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                  <SelectItem value="Expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Terms & Conditions <span className="text-red-500">*</span></Label>
              <Select value={selectedTermsId} onValueChange={handleTermsSelect}>
                <SelectTrigger className="mt-1 h-8 text-sm">
                  <SelectValue placeholder="Select Terms & Conditions" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-60">
                  {termsList.map((term: any) => (
                    <SelectItem key={term.id} value={String(term.id)}>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{term.title}</span>
                        <span className="text-xs text-gray-500 truncate max-w-[200px]">
                          {term.content.substring(0, 50)}...
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                  <div className="border-t my-1"></div>
                  <SelectItem value="add_new" className="text-blue-600 font-medium">
                    <div className="flex items-center gap-2">
                      <Plus className="h-3 w-3" />
                      <span>Add New Terms & Conditions</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Select terms & conditions for the offer letter</p>
            </div>

            {/* Preview selected terms */}
            {selectedTermsId && selectedTermsId !== "add_new" && offerConfig.terms_and_conditions && (
              <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                <p className="text-xs text-gray-600 whitespace-pre-wrap">
                  {offerConfig.terms_and_conditions}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setShowOfferModal(false)}>Cancel</Button>
            <Button size="sm" onClick={saveOfferConfig} disabled={isOfferSaving} className="bg-blue-600">
              {isOfferSaving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Terms & Conditions Modal */}
      <Dialog open={showAddTermsModal} onOpenChange={setShowAddTermsModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-base">Add New Terms & Conditions</DialogTitle>
            <DialogDescription className="text-xs">Create new terms for offer letters</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Title <span className="text-red-500">*</span></Label>
              <Input
                type="text"
                value={newTermsData.title}
                onChange={(e) => setNewTermsData({ ...newTermsData, title: e.target.value })}
                className="mt-1 h-8 text-sm"
                placeholder="e.g., Standard Offer Terms, 2024 Terms"
              />
            </div>
            <div>
              <Label className="text-xs">Content <span className="text-red-500">*</span></Label>
              <textarea
                value={newTermsData.content}
                onChange={(e) => setNewTermsData({ ...newTermsData, content: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                rows={6}
                placeholder="Enter the terms and conditions content..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setShowAddTermsModal(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSaveNewTerms} disabled={isSavingTerms} className="bg-blue-600">
              {isSavingTerms ? "Saving..." : "Save Terms"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Joining Letter Modal */}
      {/* <Dialog open={showJoiningModal} onOpenChange={setShowJoiningModal}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="text-base">Configure Joining Letter</DialogTitle>
            <DialogDescription className="text-xs">Set probation period end date</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Probation End Date <span className="text-red-500">*</span></Label>
              <Input type="date" value={joiningConfig.probationPeriod} onChange={(e) => setJoiningConfig({ probationPeriod: e.target.value })} className="mt-1 h-8 text-sm" min={employee.hire_date?.split('T')[0]} />
            </div>
            <div>
              <Label className="text-xs">Terms & Conditions</Label>
              <Input type="text" value={joiningConfig.terms_and_conditions} onChange={(e) => setJoiningConfig({ ...joiningConfig, terms_and_conditions: e.target.value })} className="mt-1 h-8 text-sm" placeholder="Enter terms and conditions..." />
            </div>
          </div>
          <DialogFooter><Button size="sm" onClick={saveJoiningConfig} className="bg-green-600">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog> */}

      {/* Joining Letter Modal - Updated with Terms & Conditions Dropdown */}
      <Dialog open={showJoiningModal} onOpenChange={setShowJoiningModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-base">Configure Joining Letter</DialogTitle>
            <DialogDescription className="text-xs">Set probation period and terms & conditions</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Probation End Date <span className="text-gray-400">(Optional)</span></Label>
                {joiningConfig.probationPeriod && (
                  <button
                    type="button"
                    onClick={() => setJoiningConfig({ ...joiningConfig, probationPeriod: "" })}
                    className="text-xs text-red-500 hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>
              <Input
                type="date"
                value={joiningConfig.probationPeriod}
                onChange={(e) => setJoiningConfig({ ...joiningConfig, probationPeriod: e.target.value })}
                className="mt-1 h-8 text-sm"
                min={employee.hire_date?.split('T')[0]}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to omit the probation period from the joining letter.
              </p>
            </div>

            <div>
              <Label className="text-xs">Terms & Conditions <span className="text-red-500">*</span></Label>
              <Select value={selectedJoiningTermsId} onValueChange={handleJoiningTermsSelect}>
                <SelectTrigger className="mt-1 h-8 text-sm">
                  <SelectValue placeholder="Select Terms & Conditions" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-60">
                  {joiningTermsList.map((term: any) => (
                    <SelectItem key={term.id} value={String(term.id)}>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{term.title}</span>
                        <span className="text-xs text-gray-500 truncate max-w-[200px]">
                          {term.content.substring(0, 50)}...
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                  <div className="border-t my-1"></div>
                  <SelectItem value="add_new" className="text-blue-600 font-medium">
                    <div className="flex items-center gap-2">
                      <Plus className="h-3 w-3" />
                      <span>Add New Terms & Conditions</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Select terms & conditions for the joining letter</p>
            </div>

            {/* Preview selected terms */}
            {selectedJoiningTermsId && selectedJoiningTermsId !== "add_new" && joiningConfig.terms_and_conditions && (
              <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                <p className="text-xs text-gray-600 whitespace-pre-wrap">
                  {joiningConfig.terms_and_conditions}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setShowJoiningModal(false)}>Cancel</Button>
            <Button size="sm" onClick={saveJoiningConfig} disabled={isJoiningSaving} className="bg-green-600">
              {isJoiningSaving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Experience / Internship-Completion Certificate Modal */}
      <Dialog open={showCertificateModal} onOpenChange={setShowCertificateModal}>
        <DialogContent className="max-w-md bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Configure {certificateLabel}</DialogTitle>
            <DialogDescription className="text-xs">Set certificate type, last working day & issue date</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-xs">Certificate Type</Label>
              <Select
                value={certificateConfig.certificateType}
                onValueChange={(value) => setCertificateConfig({ ...certificateConfig, certificateType: value })}
              >
                <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="experience">Experience Letter</SelectItem>
                  <SelectItem value="internship">Internship Completion Certificate</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Last Working Day <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={certificateConfig.lastWorkingDay}
                onChange={(e) => setCertificateConfig({ ...certificateConfig, lastWorkingDay: e.target.value })}
                className="mt-1 h-8 text-sm"
                min={employee.hire_date?.split('T')[0]}
              />
            </div>

            <div>
              <Label className="text-xs">Issue Date</Label>
              <Input
                type="date"
                value={certificateConfig.issueDate}
                onChange={(e) => setCertificateConfig({ ...certificateConfig, issueDate: e.target.value })}
                className="mt-1 h-8 text-sm"
              />
            </div>

            <div>
              <Label className="text-xs">Performance Note <span className="text-gray-400">(Optional)</span></Label>
              <textarea
                value={certificateConfig.performanceNote}
                onChange={(e) => setCertificateConfig({ ...certificateConfig, performanceNote: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                rows={2}
                placeholder="Leave empty to use a standard remark."
              />
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setShowCertificateModal(false)}>Cancel</Button>
            <Button size="sm" onClick={saveCertificateConfig} disabled={isCertificateSaving} className="bg-amber-600">
              {isCertificateSaving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Joining Terms & Conditions Modal */}
      <Dialog open={showAddJoiningTermsModal} onOpenChange={setShowAddJoiningTermsModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-base">Add New Terms & Conditions</DialogTitle>
            <DialogDescription className="text-xs">Create new terms for joining letters</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Title <span className="text-red-500">*</span></Label>
              <Input
                type="text"
                value={newJoiningTermsData.title}
                onChange={(e) => setNewJoiningTermsData({ ...newJoiningTermsData, title: e.target.value })}
                className="mt-1 h-8 text-sm"
                placeholder="e.g., Standard Joining Terms, 2024 Terms"
              />
            </div>
            <div>
              <Label className="text-xs">Content <span className="text-red-500">*</span></Label>
              <textarea
                value={newJoiningTermsData.content}
                onChange={(e) => setNewJoiningTermsData({ ...newJoiningTermsData, content: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                rows={6}
                placeholder="Enter the terms and conditions content..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setShowAddJoiningTermsModal(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSaveNewJoiningTerms} disabled={isSavingJoiningTerms} className="bg-blue-600">
              {isSavingJoiningTerms ? "Saving..." : "Save Terms"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Salary Slip Check Modal */}
      <Dialog open={showSalaryCheckModal} onOpenChange={setShowSalaryCheckModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse"></div>
              Salary Calculation Required
            </DialogTitle>
            <DialogDescription className="text-sm">
              Please calculate the salary in Payroll Records first
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-amber-800 text-sm">
                Before configuring the salary slip, you need to calculate the salary for this employee in the <strong>Payroll Records</strong> section.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSalaryCheckModal(false)}>Cancel</Button>
            <Button onClick={() => { setShowSalaryCheckModal(false); setShowSalaryModal(true); }} className="bg-blue-600">OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Salary Slip Modal */}
      <Dialog open={showSalaryModal} onOpenChange={setShowSalaryModal}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="text-base">Configure Salary Slip</DialogTitle>
            <DialogDescription className="text-xs">Select month and year</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Month</Label>
              <Select value={String(salaryConfig.month)} onValueChange={(value) => setSalaryConfig({ ...salaryConfig, month: parseInt(value) })}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{monthOptions.map((m) => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Year</Label>
              <Select value={String(salaryConfig.year)} onValueChange={(value) => setSalaryConfig({ ...salaryConfig, year: parseInt(value) })}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setShowSalaryModal(false)}>Cancel</Button>
            <Button size="sm" onClick={saveSalaryConfig} className="bg-purple-600">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="text-base">Send {emailType === "offer" ? "Offer Letter" : emailType === "joining" ? "Joining Letter" : emailType === "certificate" ? certificateLabel : "Salary Slip"}</DialogTitle>
            <DialogDescription className="text-xs">Send as PDF attachment</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Recipient Email <span className="text-red-500">*</span></Label>
              <Input type="email" value={emailRecipient} onChange={(e) => setEmailRecipient(e.target.value)} placeholder="employee@company.com" className="mt-1 h-8 text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setShowEmailModal(false)}>Cancel</Button>
            <Button size="sm" onClick={emailType === "offer" ? handleSendOfferEmail : emailType === "joining" ? handleSendJoiningEmail : emailType === "certificate" ? handleSendCertificateEmail : handleSendSalaryEmail} disabled={!emailRecipient || isSendingEmail || isSalaryEmailLoading || isCertificateLoading} className="bg-purple-600">
              {(isSendingEmail || isSalaryEmailLoading || isCertificateLoading) ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div> : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
